// Main Libraries
import React, { useEffect, useCallback, useMemo, useState } from 'react'
import {
    Grid,
    Typography,
    Box
} from '@material-ui/core';
import {
    PeopleAlt as PeopleIcon,
    Dns as QosIcon,
    Description as SdocIcon,
    Settings as CentralIcon
} from '@material-ui/icons';

// Components
import StatsCard from './components/StatsCard';
import DashboardPieChart from './components/DashboardPieChart';
import AdminListModal from './components/AdminListModal';

// Hooks and Functions
import { useStoreState, useStoreActions } from 'store'

const MainAdminDashboard = () => {
    const { admins, roles } = useStoreState(s => s.mainAdminModel)
    const { getAdmins, getRoles } = useStoreActions(
        a => a.mainAdminModel
    )

    const [modalOpen, setModalOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState('total'); // total, qos, sdoc, central
    const [modalTitle, setModalTitle] = useState('รายชื่อผู้ดูแลระบบทั้งหมด');

    const initFetch = useCallback(() => {
        getAdmins();
        getRoles();
    }, [getAdmins, getRoles])

    useEffect(() => {
        initFetch()
    }, [initFetch])

    // Compute Statistics
    const stats = useMemo(() => {
        if (!admins) return {
            total: 0,
            qos: 0,
            sdoc: 0,
            central: 0,
            active: 0,
            inactive: 0
        };

        const total = admins.length;
        const active = admins.filter(a => a.adminStatusID === 1).length;
        const inactive = total - active;

        let qos = 0;
        let sdoc = 0;
        let central = 0;

        admins.forEach(admin => {
            if (admin.adminAccess) {
                if (admin.adminAccess.some(access => parseInt(access.dataportalServiceID) === 1)) qos++;
                if (admin.adminAccess.some(access => parseInt(access.dataportalServiceID) === 2)) sdoc++;
                if (admin.adminAccess.some(access => parseInt(access.dataportalServiceID) === 3)) central++;
            }
        });

        return {
            total,
            qos,
            sdoc,
            central,
            active,
            inactive
        };

    }, [admins]);

    const handleCardClick = (filter, title) => {
        setActiveFilter(filter);
        setModalTitle(title);
        setModalOpen(true);
    };

    const filteredAdmins = useMemo(() => {
        if (!admins) return [];
        if (activeFilter === 'total') return admins;

        return admins.filter(admin => {
            if (!admin.adminAccess) return false;
            if (activeFilter === 'qos') return admin.adminAccess.some(access => parseInt(access.dataportalServiceID) === 1);
            if (activeFilter === 'sdoc') return admin.adminAccess.some(access => parseInt(access.dataportalServiceID) === 2);
            if (activeFilter === 'central') return admin.adminAccess.some(access => parseInt(access.dataportalServiceID) === 3);
            return false;
        });
    }, [admins, activeFilter]);


    const activeInactiveData = [
        { name: 'ใช้งาน (Active)', y: stats.active },
        { name: 'ไม่ใช้งาน (Inactive)', y: stats.inactive }
    ];

    const systemDistributionData = [
        { name: 'เจ้าหน้าที่ระบบ QoS', y: stats.qos },
        { name: 'เจ้าหน้าที่ระบบ SDoC', y: stats.sdoc },
        { name: 'เจ้าหน้าที่ระบบจัดการสิทธิ์', y: stats.central }
    ];

    return (
        <Grid className="p-10 h-full w-full bg-gray-50 flex flex-col min-h-screen">
            <div className="mb-8">
                <Typography variant="h4" className="font-bold text-gray-800">
                    Dashboard
                </Typography>
                <Typography variant="subtitle1" className="text-gray-500 mt-1">
                    ภาพรวมข้อมูลผู้ดูแลระบบ (Admin Overview)
                </Typography>
            </div>

            {/* Top Cards for Summary Metrics */}
            {/* Added marginBottom 16 (mb-16) as requested for more space */}
            <Grid container spacing={3} className="mb-16">
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard
                        title="เจ้าหน้าที่บนระบบทั้งหมด"
                        count={stats.total}
                        icon={<PeopleIcon style={{ fontSize: 32 }} />}
                        color="#6366F1" // Indigo
                        subText="Total Admins"
                        onClick={() => handleCardClick('total', 'รายชื่อผู้ดูแลระบบทั้งหมด')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard
                        title="เจ้าหน้าที่ระบบ QoS"
                        count={stats.qos}
                        icon={<QosIcon style={{ fontSize: 32 }} />}
                        color="#10B981" // Emerald
                        subText="Service ID: 1"
                        onClick={() => handleCardClick('qos', 'รายชื่อเจ้าหน้าที่ระบบ QoS')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard
                        title="เจ้าหน้าที่ระบบ SDoC"
                        count={stats.sdoc}
                        icon={<SdocIcon style={{ fontSize: 32 }} />}
                        color="#F59E0B" // Amber
                        subText="Service ID: 2"
                        onClick={() => handleCardClick('sdoc', 'รายชื่อเจ้าหน้าที่ระบบ SDoC')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard
                        title="เจ้าหน้าที่ระบบจัดการสิทธิ์"
                        count={stats.central}
                        icon={<CentralIcon style={{ fontSize: 32 }} />}
                        color="#EC4899" // Pink
                        subText="Service ID: 3"
                        onClick={() => handleCardClick('central', 'รายชื่อเจ้าหน้าที่ระบบจัดการสิทธิ์')}
                    />
                </Grid>
            </Grid>


            {/* Spacer */}
            <Box mt={4} />

            {/* Charts Section */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <DashboardPieChart
                        title="สัดส่วนผู้ดูแลระบบตามระบบ (Admin by System)"
                        data={systemDistributionData}
                        colors={['#10B981', '#F59E0B', '#EC4899']}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <DashboardPieChart
                        title="สถานะการใช้งาน (Active vs Inactive)"
                        data={activeInactiveData}
                        colors={['#3B82F6', '#EF4444']}
                    />
                </Grid>
            </Grid>

            <AdminListModal
                open={modalOpen}
                handleClose={() => setModalOpen(false)}
                title={modalTitle}
                admins={filteredAdmins}
                roles={roles}
                activeFilter={activeFilter}
            />
        </Grid >
    )
}

export default MainAdminDashboard;
